import { AccountsReceivableSummary } from '@/api/business/unsettledSecurities/type'
import ButtonWithTooltip from '@/components/ButtonWithTooltip'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { getDateFromId } from '@/utils/date'
import { currencyFormat } from '@/utils/stringFormatter'
import { format } from 'date-fns'
import { ArrowDownToLine, FileText } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSummaryByClassificadoSpreadsheets } from './useSummaryByClassificadoSpreadsheets'
import ViewPDF from './viewPDF'

interface SummaryByClassificadoDialogProps {
    isOpen: boolean
    onClose: () => void
    data: AccountsReceivableSummary[]
}

type EmpresaSummary = {
    empresa: string
    total: number
}

type MesAnoSummary = {
    mesAno: string
    sortKey: number
    empresas: EmpresaSummary[]
    total: number
}

type ClassificadoSummary = {
    classificado: string
    periodos: MesAnoSummary[]
    total: number
}

const getMesAnoFromVencimentoReal = (skVencimentoReal: number | null | undefined) => {
    if (!skVencimentoReal) {
        return { mesAno: 'Sem data', sortKey: Number.MAX_SAFE_INTEGER }
    }

    try {
        const date = getDateFromId(Number(skVencimentoReal))
        return {
            mesAno: format(date, 'MM/yyyy'),
            sortKey: Number(skVencimentoReal),
        }
    } catch {
        return { mesAno: 'Sem data', sortKey: Number.MAX_SAFE_INTEGER }
    }
}

const SummaryByClassificadoDialog = ({
    isOpen,
    onClose,
    data,
}: SummaryByClassificadoDialogProps) => {
    const [selectedPeriod, setSelectedPeriod] = useState('all')
    const [isPdfOpen, setIsPdfOpen] = useState(false)
    const { onDownload } = useSummaryByClassificadoSpreadsheets()

    const availablePeriods = useMemo(() => {
        const periods = new Map<string, number>()

        data.filter(item => Number(item.DD_DIAS_JUROS) < 0).forEach((item) => {
            const { mesAno, sortKey } = getMesAnoFromVencimentoReal(item.SK_VENCIMENTO_REAL)
            periods.set(mesAno, Math.min(periods.get(mesAno) ?? Number.MAX_SAFE_INTEGER, sortKey))
        })

        return Array.from(periods.entries())
            .map(([mesAno, sortKey]) => ({ mesAno, sortKey }))
            .sort((a, b) => a.sortKey - b.sortKey)
    }, [data])

    const summary = useMemo(() => { 
        const byClassificado = new Map<string, Map<string, Map<string, number>>>()
        const mesAnoSortKeys = new Map<string, number>()

        data.filter(item => Number(item.DD_DIAS_JUROS) < 0).forEach((item) => {
            const classificado = item.CLASSIFICADO || 'Sem classificação'
            const { mesAno, sortKey } = getMesAnoFromVencimentoReal(item.SK_VENCIMENTO_REAL)

            if (selectedPeriod !== 'all' && mesAno !== selectedPeriod) {
                return
            }

            const empresa = item.ABREVIATURA_EMPRESA || 'Sem empresa'
            const valor = Number(item.VL_TOTAL_COM_JUROS) || 0

            if (!mesAnoSortKeys.has(mesAno) || sortKey < (mesAnoSortKeys.get(mesAno) ?? Number.MAX_SAFE_INTEGER)) {
                mesAnoSortKeys.set(mesAno, sortKey)
            }

            if (!byClassificado.has(classificado)) {
                byClassificado.set(classificado, new Map())
            }

            const byPeriodo = byClassificado.get(classificado)!
            if (!byPeriodo.has(mesAno)) {
                byPeriodo.set(mesAno, new Map())
            }

            const byEmpresa = byPeriodo.get(mesAno)!
            byEmpresa.set(empresa, (byEmpresa.get(empresa) || 0) + valor)
        })

        const result: ClassificadoSummary[] = Array.from(byClassificado.entries())
            .map(([classificado, periodosMap]) => {
                const periodos = Array.from(periodosMap.entries())
                    .map(([mesAno, empresasMap]) => {
                        const empresas = Array.from(empresasMap.entries())
                            .map(([empresa, total]) => ({ empresa, total }))
                            .sort((a, b) => a.empresa.localeCompare(b.empresa))

                        const total = empresas.reduce((acc, item) => acc + item.total, 0)

                        return {
                            mesAno,
                            sortKey: mesAnoSortKeys.get(mesAno) ?? Number.MAX_SAFE_INTEGER,
                            empresas,
                            total,
                        }
                    })
                    .sort((a, b) => a.sortKey - b.sortKey)

                const total = periodos.reduce((acc, item) => acc + item.total, 0)

                return { classificado, periodos, total }
            })
            .sort((a, b) => a.classificado.localeCompare(b.classificado))

        return result
    }, [data, selectedPeriod])

    const totalsByMonth = useMemo(() => {
        const byMonth = new Map<string, { total: number; sortKey: number }>()

        summary.forEach((group) => {
            group.periodos.forEach((periodo) => {
                const current = byMonth.get(periodo.mesAno)
                byMonth.set(periodo.mesAno, {
                    total: (current?.total || 0) + periodo.total,
                    sortKey: periodo.sortKey,
                })
            })
        })

        return Array.from(byMonth.entries())
            .map(([mesAno, { total, sortKey }]) => ({ mesAno, total, sortKey }))
            .sort((a, b) => a.sortKey - b.sortKey)
    }, [summary])

    const grandTotal = useMemo(
        () => totalsByMonth.reduce((acc, item) => acc + item.total, 0),
        [totalsByMonth]
    )

    return (
        <>
            <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DialogContent className="max-w-[90vw] max-h-[95vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Resumo por Classificado</DialogTitle>
                    </DialogHeader>

                    <div className="flex items-center justify-between gap-4">
                        <div className="w-[220px]">
                            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Filtrar por mês/ano" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos os períodos</SelectItem>
                                    {availablePeriods.map(({ mesAno }) => (
                                        <SelectItem key={mesAno} value={mesAno}>
                                            {mesAno}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <ButtonWithTooltip
                                tooltipContent={<p className="text-white">Visualizar PDF</p>}
                                variant="ghost"
                                disabled={summary.length === 0}
                                onClick={() => setIsPdfOpen(true)}
                            >
                                <FileText size={18} />
                            </ButtonWithTooltip>
                            <div className="w-[1px] h-4 bg-neutral-300" />
                            <ButtonWithTooltip
                                tooltipContent={<p className="text-white">Download Excel</p>}
                                variant="ghost"
                                disabled={summary.length === 0}
                                onClick={() => onDownload(summary, totalsByMonth, grandTotal)}
                            >
                                <ArrowDownToLine size={18} />
                            </ButtonWithTooltip>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto space-y-4 pr-1">
                        {summary.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                Nenhum dado disponível para o resumo.
                            </p>
                        )}

                        {totalsByMonth.length > 0 && (
                            <div className="border border-neutral-200 rounded-md overflow-hidden">
                                <div className="flex items-center justify-between bg-neutral-100 px-4 py-2">
                                    <span className="font-semibold text-sm">Total por Mês</span>
                                    <span className="font-semibold text-sm tabular-nums">
                                        {currencyFormat(grandTotal)}
                                    </span>
                                </div>
                                <div className="flex gap-3 overflow-x-auto p-3">
                                    {totalsByMonth.map((periodo) => (
                                        <div
                                            key={`total-mes-${periodo.mesAno}`}
                                            className="min-w-[180px] flex-1 border border-neutral-200 rounded-md bg-neutral-50 px-4 py-3"
                                        >
                                            <p className="text-sm font-medium">{periodo.mesAno}</p>
                                            <p className="text-base font-semibold tabular-nums mt-1">
                                                {currencyFormat(periodo.total)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {summary.map((group) => (
                            <div
                                key={group.classificado}
                                className="border border-neutral-200 rounded-md overflow-hidden"
                            >
                                <div className="flex items-center justify-between bg-neutral-100 px-4 py-2">
                                    <span className="font-semibold text-sm">
                                        {group.classificado}
                                    </span>
                                    <span className="font-semibold text-sm">
                                        {currencyFormat(group.total)}
                                    </span>
                                </div>

                                <div className="flex gap-3 overflow-x-auto p-3">
                                    {group.periodos.map((periodo) => (
                                        <div
                                            key={`${group.classificado}-${periodo.mesAno}`}
                                            className="min-w-[260px] flex-1 border border-neutral-200 rounded-md overflow-hidden"
                                        >
                                            <div className="flex items-center justify-between bg-neutral-50 px-4 py-2 text-sm">
                                                <span className="font-medium">
                                                    {periodo.mesAno}
                                                </span>
                                                <span className="font-medium tabular-nums">
                                                    {currencyFormat(periodo.total)}
                                                </span>
                                            </div>

                                            <div className="divide-y divide-neutral-50">
                                                {periodo.empresas.map((empresa) => (
                                                    <div
                                                        key={`${group.classificado}-${periodo.mesAno}-${empresa.empresa}`}
                                                        className="flex items-center justify-between px-4 py-2 pl-4 text-sm"
                                                    >
                                                        <span className="text-muted-foreground">
                                                            {empresa.empresa}
                                                        </span>
                                                        <span className="tabular-nums">
                                                            {currencyFormat(empresa.total)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {summary.length > 0 && (
                        <div className="flex items-center justify-between border-t pt-3 mt-2">
                            <span className="font-semibold">Total Geral</span>
                            <span className="font-semibold tabular-nums">
                                {currencyFormat(grandTotal)}
                            </span>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ViewPDF
                isOpen={isPdfOpen}
                onClose={() => setIsPdfOpen(false)}
                summary={summary}
                totalsByMonth={totalsByMonth}
                grandTotal={grandTotal}
                selectedPeriod={selectedPeriod}
            />
        </>
    )
}

export default SummaryByClassificadoDialog
